import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { markDocumentSettled } from '@/shared/lib/document-navigation';
import { TooltipProvider } from '@/shared/ui/tooltip';
import { Toaster } from '@/shared/ui/toaster';
import { LanguageProvider } from '@/shared/lib/language';
import { DateFormatProvider } from '@/shared/lib/date-format';
import { NumberFormatProvider } from '@/shared/lib/number-format';
import { SessionExpiryWatcher } from '@/shared/components/SessionExpiryWatcher';
import { ConsoleLayout } from '@/shared/components/ConsoleLayout';
import Login from '../pages/login';
import Console from '../pages/console';
import AccountHolderSelectionPage from '../pages/account-holder-selection';
import AccountSelectionPage from '../pages/account-selection';
import AccountCreationPage from '../pages/account-creation';
import ForgotPassword from '../pages/forgot-password';
import ResetPassword from '../pages/reset-password';
import UserInvite from '../pages/user-invite';
import Integrations from '../pages/console/integrations';
import NewIntegration from '../pages/console/integrations/new';
import Investors from '../pages/console/investors';
import InvestorDetail from '../pages/console/investors/detail';
import PortfolioList from '../pages/console/portfolio';
import PortfolioDetail from '../pages/console/portfolio/detail';
import AccessManagement from '../pages/console/access';
import Settings from '../pages/console/settings';

const queryClient = new QueryClient();

function App() {
  useEffect(() => {
    markDocumentSettled();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <DateFormatProvider>
          <NumberFormatProvider>
            <TooltipProvider>
              <Toaster />
              <Router
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
                <SessionExpiryWatcher />
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/user-invite/:inviteId" element={<UserInvite />} />
                  <Route path="/account-holder-selection" element={<AccountHolderSelectionPage />} />
                  <Route path="/account-selection" element={<AccountSelectionPage />} />
                  <Route path="/account-creation" element={<AccountCreationPage />} />
                  <Route element={<ConsoleLayout />}>
                    <Route path="/console" element={<Console />} />
                    <Route path="/console/integrations" element={<Integrations />} />
                    <Route path="/console/integrations/new" element={<NewIntegration />} />
                    <Route path="/console/investors" element={<Investors />} />
                    <Route path="/console/investors/:investorId" element={<InvestorDetail />} />
                    <Route path="/console/portfolio" element={<PortfolioList />} />
                    <Route path="/console/portfolio/:portfolioId" element={<PortfolioDetail />} />
                    <Route path="/console/access" element={<AccessManagement />} />
                    <Route path="/console/settings" element={<Settings />} />
                  </Route>
                  <Route path="/" element={<Login />} />
                </Routes>
              </Router>
            </TooltipProvider>
          </NumberFormatProvider>
        </DateFormatProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
}

export default App;
